import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ADIProductShareLink from "@NA/Components/ADIProductShareLink";
import CrossIcon from "@NA/Icons/CrossIcon";
import ProductImageCarousel, { ProductImageCarouselStyles } from "@NA/Overrides/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@NA/Overrides/Components/ProductPrimaryImage";
import ADIProductCarousal from "@NA/Widgets/ADIProductCarousal";

import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, Hidden, Link, Typography } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ADIExclusivelogo from "@NA/Icons/PDPDocuments/ADIExclusivelogo";
import translate from "@insite/client-framework/Translate";
import ADIColors from "@NA/Utils/ADIColors";
import Img from "@insite/mobius/Img";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getKeyValuePairsFromHtmlString } from "@NA/Utils/Utility";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState, ownProps: HasProduct) => ({
    selectedImage: getSelectedImage(state, ownProps.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    selectedMvpImage: state.ADIGlobalData.updatePDPTab.selectedMVPImage,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;
    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    brandStyles?: ProductBrandStyles;
    titleText?: TypographyProps;
    productPartsDetails?: TypographyProps;
    modalCarousalWrapper?: InjectableCss;
    imageGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    brandName?: TypographyProps;
    shareLinkWrapper?: InjectableCss;
    adiLogo?: InjectableCss;
    adiLogoMobile?: InjectableCss;
    adiLogoWrapper?: InjectableCss;
    adiLogoWrapperMobile?: InjectableCss;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    adiLogo: {
        css: css`
            > svg {
                margin: 0;
                padding: 0;
                display: block;
                transform: initial;
                left: 0;
                max-width: initial;
                position: relative;
                height: 15px;
                width: auto;
                vertical-align: baseline;
                top: 2px;
            }
        `,
    },
    adiLogoMobile: {
        css: css`
            > svg {
                margin: 0;
                padding: 0;
                display: block;
                transform: initial;
                left: 0;
                max-width: initial;
                position: relative;
                height: 15px;
                width: auto;
                vertical-align: baseline;
                top: 2px;
            }
        `,
    },
    fullPrimaryImageModal: {
        sizeVariant: "large",
        closeButtonIconProps: {
            src: CrossIcon,
            size: 20,
        },
        closeButtonProps: {
            hoverMode: "none",
            activeMode: "none",
            css: css`
                padding-top: 10px;
                padding-right: 10px;
            `,
        },
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                max-height: initial !important;
                border-radius: 7px;
                width: 1116px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            max-width: 90% !important;
                            max-height: 70vh !important;
                        `,
                        css`
                            max-width: 90% !important;
                            max-height: 70vh !important;
                        `,
                        null,
                        null,
                    ])}
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
                padding: 25px 45px 43px;
            `,
        },
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleText: {
        variant: "h4",
        css: css`
            margin-bottom: 0px;
            color: ${getColor("text.main")};
            font-weight: 600;
            line-height: 27px;
        `,
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-width: 400px !important;
                    max-height: 400px !important;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-width: 450px !important;
                max-height: 450px !important;
            `,
        },
        centeringWrapper: {
            css: css`
                height: 100%;
            `,
        },
    },
    productPartsDetails: {
        css: css`
            span {
                margin-right: 10px;
                font-size: ${getProp("theme.typography.headerSecondary.size")};
            }
        `,
    },
    modalCarousalWrapper: {
        css: css`
            margin-top: 10px;
            img {
                width: 84px !important;
                height: 84px !important;
                max-width: 84px !important;
                max-height: 84px !important;
            }
        `,
    },
    imageGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            justify-content: center;
        `,
    },
    contentGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
            text-align: left;
            padding-left: "30px";
        `,
    },
    brandName: {
        css: css`
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            margin-bottom: 5px;
            display: block;
            font-weight: 600;
        `,
    },
    shareLinkWrapper: {
        css: css`
            position: absolute;
            right: 15px;
            top: 5px;
        `,
    },
    adiLogoWrapper: {
        css: css`
        display:flex;
        margin-top: 9px;
        .exclusiveText{
            font-size: 18px;
            line-height: 18px;
            color: ${ADIColors.black};
            margin-left: 7.2px;
            font-weight: normal;
        }
            .multipleImages{
            margin-bottom: 30px !important;
            }
            .ovrcText{
            width:190px;
            margin-left:5px;
        }
        .OvrCPipe {
	    color: #bbb;
        margin: 0 5px;
        line-height: 20px;
        margin-left: 8px !important;
        font-size: 25px !important;
        font-weight: 100 !important;
        }
     .OvrCImg .imgEle {
	 display: inline-block;
	 padding: 0;
	 max-width: 48px;
	 width: 100%;
     cursor:text !important;
     margin-right: 5px !important;
}
     .imgText {
	 color: #2d2926;
	 font-size: 18px;
	 font-weight: normal;
	 display: inline-block;
     margin-left: 2px;
	 line-height: normal;
	 position: relative;
     vertical-align: text-bottom !important;
} 
     @media screen and (max-width: 767px) {
     .imgText {
		 font-size: 16px;
		 margin-top: 0px;
		 margin-left: 0px;
    }
         .OvrCImg .imgEle {
		 max-width: 40px;
	}
     }

   
        .tooltip-wrapper {
        position: absolute;
                    .tooltip-icon {
                        position: relative !important;
                        display: inline-block;
                        height: 17px;
                        width: 17px;
                        vertical-align: bottom;
                        font-style: normal;
                        margin-left: 2px;
                        vertical-align: middle;
                        padding-bottom: 5px;
                        cursor: pointer;
                        margin-bottom: 0.25em;

                        &:after {
                            content: "";
                            position: absolute;
                            left: auto;
                            right: -5px;
                            top: 2px;
                            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/tooltip_icon_default.svg);
                            background-repeat: no-repeat;
                            background-size: 17px 17px;
                            background-position: center;
                            cursor: pointer;
                            height: 17px;
                            width: 17px;
                        }

                        &.tooltip-active {
                            &:after {
                                content: "";
                                position: absolute;
                                left: auto;
                                right: -5px;
                                top: 2px;
                                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/tooltip_icon_active.svg);
                                background-repeat: no-repeat;
                                background-size: 17px 17px;
                                background-position: center;
                                cursor: pointer;
                                height: 17px;
                                width: 17px;
                            }

                            .tooltip-text {
                                display: block;
                            }
                        }
.tooltip-text.only-OvrC{
margin-left: -215px !important;
}
@media screen and (max-width: 1180px) {
.tooltip-text.only-OvrC {
        margin-left: -135px !important;
    }
        .tooltip-text.only-OvrC:after{
            left: calc(32% - 16px) !important;
        }  
} 

                        .tooltip-text {
                            padding: 10px 15px;
                            border: 1px solid #bbb;
                            width: 430px;
                            border-radius: 5px;
                            margin-left: -214px;
                            top: -96px !important;
                            bottom: 96px;
                            font-size: 12px;
                            font-weight: normal;
                            font-stretch: normal;
                            font-style: normal;
                            line-height: normal;
                            -webkit-letter-spacing: normal;
                            -moz-letter-spacing: normal;
                            -ms-letter-spacing: normal;
                            letter-spacing: normal;
                            text-align: left;
                            color: #2d2926;
                            display: none;
                            background: #ffffff;
                            position: relative;
                            left: 19px;

                            > a {
                                font-size: 14px;
                                font-weight: normal;
                                font-stretch: normal;
                                font-style: normal;
                                line-height: normal;
                                letter-spacing: normal;
                                color: #2d2926 !important;
                                text-decoration: underline !important;

                                &:hover {
                                    font-size: 14px;
                                    font-weight: normal;
                                    font-stretch: normal;
                                    font-style: normal;
                                    line-height: normal;
                                    letter-spacing: normal;
                                    color: #2d2926 !important;
                                }
                            }

                            &:before {
                                top: auto;
                                left: 125px;
                                bottom: -10px;
                                border-right: 10px solid transparent;
                                border-bottom: 10px solid #555;
                                border-left: 10px solid transparent;
                                border-bottom-color: #bbb;
                                transform: rotate(180deg);
                            }
                        }
                    }
                }

            }

            .tooltip-icon .tooltip-text:after {
                content: "";
                position: absolute;
                left: calc(50% - 15px);
                bottom: -8px;
                border: 8px solid #fff;
                box-shadow: -1px 1px 0 0 #bbb;
                border-color: transparent transparent #fff #fff;
                -webkit-transform: rotate(315deg);
                -ms-transform: rotate(315deg);
                transform: rotate(315deg);
            }
            .tooltip-wrapper .tooltip-icon .tooltip-text > a span {
                font-size: 14px;
                font-weight: normal;
                font-stretch: normal;
                font-style: normal;
                line-height: normal;
                letter-spacing: normal;
                color: #2d2926 !important;
                text-decoration: underline !important;
            }
            .tooltip-wrapper .tooltip-icon .tooltip-text.only-OvrC {
                margin-left: -135px !important;
            }
            .tooltip-icon .tooltip-text.only-OvrC:after{
                left: calc(32% - 16px) !important;
            }
            @media only screen and (max-width: 767px) {
                .tooltip-wrapper {
                    position: unset !important;
                    left: unset !important;
                }
            }
            @media only screen and (min-width: 768px) and (max-width: 1080px) {
                .tooltip-wrapper .tooltip-icon {
                    margin-left: 2px;
                }
                
            }  
            @media screen and (max-width: 767px) {
                .tooltip-wrapper .tooltip-icon .tooltip-text{
                    top:auto !important;
                }
            }
            @media screen and (max-width: 1200px) {
                .tooltip-wrapper .tooltip-icon .tooltip-text{
                    width: 430px;
                    top: 30px !important;
                    margin-left: -213px !important;
                }
                .tooltip-icon .tooltip-text:after{
                    bottom: 75px !important;
                    transform: rotate(134deg) !important;
                }
            }
                @media (min-width: 768px) and (max-width: 1200px) {
                    .tooltip-icon .tooltip-text:after{
                        top: -7px !important;
                        bottom: auto !important;
                    }
                }

                
        `,
    },
    adiLogoWrapperMobile: {
        css: css`
        display:flex;
        margin-top: 9px;
        .exclusiveText{
            font-size: 18px;
            line-height: 18px;
            color: ${ADIColors.black};
            margin-left: 7.2px;
            font-weight: normal;
        }
                .ovrcText{
            width:190px;
            margin-left:5px;
        }
            .OvrCPipe {
	    color: #bbb;
        margin: 0 5px;
        line-height: 20px;
        margin-left: 8px !important;
        font-size: 25px !important;
        font-weight: 100 !important;
        }
     .OvrCImg .imgEle {
	 display: inline-block;
	 padding: 0;
	 max-width: 48px;
	 width: 100%;
     cursor:text !important;
     margin-right: 5px !important;
}
     
     .imgText {
	 color: #2d2926;
	 font-size: 18px;
	 font-weight: normal;
	 display: inline-block;
     margin-left: 2px;
	 line-height: normal;
	 position: relative;
     vertical-align: text-bottom !important;
}
     @media screen and (max-width: 767px) {
     .imgText {
		 font-size: 16px;
		 margin-top: 0px;
		 margin-left: 0px;
    }
         .OvrCImg .imgEle {
		 max-width: 40px;
	}
     }

    
     
        .tooltip-wrapper {
        position: absolute;
                    .tooltip-icon {
                        position: relative !important;
                        display: inline-block;
                        height: 17px;
                        width: 17px;
                        vertical-align: bottom;
                        font-style: normal;
                        margin-left: 2px;
                        vertical-align: middle;
                        padding-bottom: 5px;
                        cursor: pointer;
                        margin-bottom: 0.25em;

                        &:after {
                            content: "";
                            position: absolute;
                            left: auto;
                            right: -5px;
                            top: 2px;
                            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/tooltip_icon_default.svg);
                            background-repeat: no-repeat;
                            background-size: 17px 17px;
                            background-position: center;
                            cursor: pointer;
                            height: 17px;
                            width: 17px;
                        }

                        &.tooltip-active {
                            &:after {
                                content: "";
                                position: absolute;
                                left: auto;
                                right: -5px;
                                top: 2px;
                                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/tooltip_icon_active.svg);
                                background-repeat: no-repeat;
                                background-size: 17px 17px;
                                background-position: center;
                                cursor: pointer;
                                height: 17px;
                                width: 17px;
                            }

                            .tooltip-text {
                                display: block;
                            }
                        }

                        .tooltip-text {
                            z-index:1;
                            padding: 10px 15px;
                            border: 1px solid #bbb;
                            width: 190px;
                            border-radius: 5px;
                            margin-left: -106px;
                            top: -96px !important;
                            bottom: -30px;
                            font-size: 12px;
                            font-weight: normal;
                            font-stretch: normal;
                            font-style: normal;
                            line-height: normal;
                            -webkit-letter-spacing: normal;
                            -moz-letter-spacing: normal;
                            -ms-letter-spacing: normal;
                            letter-spacing: normal;
                            text-align: left;
                            color: #2d2926;
                            display: none;
                            background: #ffffff;
                            position: relative;
                            left: 19px;

                            > a {
                                font-size: 14px;
                                font-weight: normal;
                                font-stretch: normal;
                                font-style: normal;
                                line-height: normal;
                                letter-spacing: normal;
                                color: #2d2926 !important;
                                text-decoration: underline !important;

                                &:hover {
                                    font-size: 14px;
                                    font-weight: normal;
                                    font-stretch: normal;
                                    font-style: normal;
                                    line-height: normal;
                                    letter-spacing: normal;
                                    color: #2d2926 !important;
                                }
                            }

                            &:before {
                                top: auto;
                                left: 125px;
                                bottom: -10px;
                                border-right: 10px solid transparent;
                                border-bottom: 10px solid #555;
                                border-left: 10px solid transparent;
                                border-bottom-color: #bbb;
                                transform: rotate(180deg);
                            }
                        }
                    }
                }
            }
            .tooltip-icon .tooltip-text:after {
                content: "";
                position: absolute;
                left: calc(50% - 15px);
                bottom: -8px;
                border: 8px solid #fff;
                box-shadow: -1px 1px 0 0 #bbb;
                border-color: transparent transparent #fff #fff;
                -webkit-transform: rotate(315deg);
                -ms-transform: rotate(315deg);
                transform: rotate(315deg);
            }
            .tooltip-wrapper .tooltip-icon .tooltip-text > a span {
                font-size: 14px;
                font-weight: normal;
                font-stretch: normal;
                font-style: normal;
                line-height: normal;
                letter-spacing: normal;
                color: #2d2926 !important;
                text-decoration: underline !important;
            }
                @media only screen and (max-width: 767px) {
                .tooltip-wrapper {
                    position: unset !important;
                    left: unset !important;
                }
                    .tooltip-text {
		 width: 345px !important;
		 left: -41px;
		 right: auto;
		 margin: auto;
		 transform: translate(-50%, 2%);
		 top: auto;
		 bottom: 32px;
	}
         .tooltip-icon .tooltip-text:after{
         bottom: auto !important;
         top: -8px;
         left: calc(81% - 15px) !important;
         transform: rotate(135deg) !important; 
         }
         .tooltip-icon .tooltip-text.only-OvrC:after{
         left: calc(36% - 15px) !important;
        }
         .tooltip-text.only-OvrC{
            margin-left: 50px !important;
        }
            }
            
            @media screen and (max-width: 1200px) {
                .tooltip-wrapper .tooltip-icon .tooltip-text{
                    width: 430px;
                    top: 30px !important;
                    margin-left: -213px !important;
                }
                .tooltip-icon .tooltip-text:after{
                    bottom: 75px !important;
                    transform: rotate(134deg) !important;
                }
                .tooltip-wrapper .tooltip-icon .tooltip-text.only-OvrC{
                    margin-left: -135px !important;
                }
                .tooltip-wrapper .tooltip-icon .tooltip-text.only-OvrC:after{
                    left: calc(50% - 93px) !important;
                }
            }
                @media (min-width: 768px) and (max-width: 1200px) {
                    .tooltip-icon .tooltip-text:after{
                        top: -7px !important;
                        bottom: auto !important;
                    }
                }
            @media screen and (max-width: 767px) {
                .tooltip-wrapper .tooltip-icon .tooltip-text{
                    top:auto !important;
                    margin-left: -90px !important;
                }
                .tooltip-icon .tooltip-text:after{
                    bottom: 91px !important;
                    left: calc(77% - 15px) !important;
                }
                .tooltip-wrapper .tooltip-icon .tooltip-text.only-OvrC{
                    top:auto !important;
                    margin-left: 51px !important;
                }
                .tooltip-wrapper .tooltip-icon .tooltip-text.only-OvrC:after{
                    left: calc(36% - 15px) !important;
                }
                .tooltip-icon .tooltip-text:after{
                    bottom: 100px !important;
                    transform: rotate(135deg) !important;
                }
            }
            @media only screen and (min-device-width: 375px) and (max-device-width: 760px) and (-webkit-min-device-pixel-ratio: 3) { 
                .tooltip-icon .tooltip-text:after{
                    border: 1px solid #bbb;
                    border-bottom: none;
                    border-right: none;
                    height: 16px;
                    width: 16px;
                    top: 0.5px;
                    transform: translatey(-9px) rotate(45deg) !important;
                    background-color: #fff;
                    box-shadow: none;
                } 
            }

        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage: React.FC<Props> = ({
    product,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
}) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = React.useState(false);
    const [isTabView, setIsTabView] = React.useState(false);
    const [modelPrimaryImage, setmodelPrimaryImage] = React.useState<ImageModel[]>([]);
    const [modelPrimaryImageIndex, setModelPrimaryImageindex] = React.useState(0);
    const [ishover, setishover] = React.useState(false);
    const [isOvrCFlag, setOvrCFlag] = React.useState(false);
    const [isCarousel, setCarousel] = React.useState(false);

    const imagePath = "https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Brands/OvrC_logo.svg";
    const altText = "";
    // Below 3 lines are being used by P21 ---- P21 Change
    let OvrCFlag = false;
    const htmlString: any = product && product.specifications && product.specifications.filter(item => item.name == "General Information")[0]?.htmlContent;
    OvrCFlag = getKeyValuePairsFromHtmlString(htmlString, "OvrC Enabled", "Yes"); // Utility call to get boolean value 

    let isMultiImage = (product && product.images && product.images.length > 1) ? true : false;

    const tooltipUpdate = () => {
        setishover(true);
    };

    const tooltipDisable = () => {
        setishover(false);
    };

    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        if (!fullPrimaryImageModalIsOpen) {
            setSelectedImageIndex({ index });
            setCarousel(true);
        }
        setmodelPrimaryImage(product.images!);
        setModelPrimaryImageindex(index);
    };

    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    React.useEffect(() => {
        window.addEventListener("resize", onWindowResize);
        onWindowResize();
        let specData: any = [];
        try {
            const titleElems = document.querySelectorAll(".spec-title");
            const valueElems = document.querySelectorAll(".spec-value");
            specData = Array.from(titleElems).map((titleElem, index) => {
                const valueElem = valueElems[index];
                return {
                    title: titleElem?.textContent?.trim() || "",
                    value: valueElem?.textContent?.trim() || "",
                };
            });
        } catch (error) {
            console.error("Error extracting spec data:", error);
        }

        // Check if "OvrC Enabled" exists and set the flag
        let ovrCSpec = specData && specData.find((spec: any) => spec.title === "OvrC Enabled");
        if (ovrCSpec) {
            setOvrCFlag(ovrCSpec.value === "Yes");
        }

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);
    const onWindowResize = () => {
        if (window.innerWidth >= 1169) {
            setIsTabView(false);
        } else {
            setIsTabView(true);
        }
    };
    const adiLogo = () => {
        const adiBrands = product.properties["adI_ExclusiveBrands"];
        const adiExclBrandsArray = adiBrands != null && adiBrands != "" ? adiBrands.split(",") : "";
        const brandId = product.properties["branD_ID"] != null ? product.properties["branD_ID"].trim() : "";
        if (brandId != null && brandId != "" && adiExclBrandsArray != "") {
            for (var i = 0; i < adiExclBrandsArray.length; i++) {
                if (adiExclBrandsArray[i].trim() == brandId) {
                    return true;
                }
            }
            return false;
        } else {
            return false;
        }
    };

    return (
        <>
            {!isTabView ? (
                <>
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        extendedStyles={styles.primaryImage}
                    />

                    <StyledWrapper {...styles.adiLogoWrapper}>
                        {adiLogo() && (
                            <>
                                <Typography {...styles.adiLogo} className={isMultiImage ? "multipleImages" : ""}>
                                    <ADIExclusivelogo />
                                </Typography>
                                <Typography className="exclusiveText">{translate("- Exclusive")}</Typography>
                            </>
                        )}
                        {isOvrCFlag && adiLogo() && (
                            <Typography as="span" className={isMultiImage ? "OvrCPipe multipleImages" : "OvrCPipe"}>
                                {" "}
                                |
                            </Typography>
                        )}
                        {isOvrCFlag && (
                            <Typography className={isMultiImage ? "ovrcText multipleImages" : "ovrcText"}>
                                <Typography as="span" className="OvrCImg">
                                    <Img src={imagePath} altText={altText} className="imgEle" />
                                    <Typography as="span" className="imgText">
                                        Enabled
                                    </Typography>
                                </Typography>
                                <Typography as="span" className="tooltip-wrapper">
                                    <Typography
                                        as="i"
                                        onMouseEnter={tooltipUpdate}
                                        onMouseLeave={tooltipDisable}
                                        className={ishover ? "tooltip-icon tooltip-active" : "tooltip-icon"}
                                    >
                                        <Typography
                                            as="span"
                                            className={
                                                isOvrCFlag && adiLogo() ? "tooltip-text" : "tooltip-text only-OvrC"
                                            }
                                        >
                                            <Typography as="span">{siteMessage("OvrC Message")}</Typography>
                                        </Typography>
                                    </Typography>
                                </Typography>
                            </Typography>
                        )}
                    </StyledWrapper>

                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                        inModal={false}
                        setFullImageModalIsOpen={setFullImageModalIsOpen}
                    />
                </>
            ) : (
                <>
                    <StyledWrapper {...styles.shareLinkWrapper}>
                        <ADIProductShareLink />
                    </StyledWrapper>

                    <StyledWrapper {...styles.adiLogoWrapperMobile}>
                        {adiLogo() && (
                            <>
                                <Typography {...styles.adiLogoMobile}>
                                    <ADIExclusivelogo />
                                </Typography>
                                <Typography className="exclusiveText">{translate("- Exclusive")}</Typography>
                            </>
                        )}
                        {isOvrCFlag && adiLogo() && (
                            <Typography as="span" className="OvrCPipe">
                                {" "}
                                |
                            </Typography>
                        )}
                        {isOvrCFlag && (
                            <Typography className="ovrcText">
                                <Typography as="span" className="OvrCImg">
                                    <Img src={imagePath} altText={altText} className="imgEle" />
                                    <Typography as="span" className="imgText">
                                        Enabled
                                    </Typography>
                                </Typography>
                                <Typography as="span" className="tooltip-wrapper">
                                    <Typography
                                        as="i"
                                        onMouseEnter={tooltipUpdate}
                                        onMouseLeave={tooltipDisable}
                                        className={ishover ? "tooltip-icon tooltip-active" : "tooltip-icon"}
                                    >
                                        <Typography
                                            as="span"
                                            className={
                                                isOvrCFlag && adiLogo() ? "tooltip-text" : "tooltip-text only-OvrC"
                                            }
                                        >
                                            <Typography as="span">{siteMessage("OvrC Message")}</Typography>
                                        </Typography>
                                    </Typography>
                                </Typography>
                            </Typography>
                        )}
                    </StyledWrapper>
                    <ADIProductCarousal
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        isTabView={isTabView}
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        setFullImageModalIsOpen={setFullImageModalIsOpen}
                    />
                </>
            )}

            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <GridContainer>
                    <GridItem {...styles.imageGridItem}>
                        <ProductPrimaryImage
                            product={product}
                            image={
                                modelPrimaryImage[modelPrimaryImageIndex] !== undefined &&
                                selectedImageIndex !== modelPrimaryImageIndex
                                    ? modelPrimaryImage[modelPrimaryImageIndex]
                                    : image
                            }
                            useLargeImage={true}
                            extendedStyles={styles.fullPrimaryImage}
                            inModal={true}
                        />
                    </GridItem>
                    <GridItem {...styles.contentGridItem}>
                        <Hidden below="lg">
                            <StyledWrapper>
                                <Typography {...styles.brandName}>{product.brand?.name}</Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography {...styles.titleText} data-test-selector="ProductDetailsPageTitle">
                                    {product.productTitle}
                                </Typography>
                                <StyledWrapper {...styles.productPartsDetails}>
                                    <Typography as="span">{product.detail?.modelNumber}</Typography>
                                    <Typography as="span">|</Typography>
                                    <Typography as="span">{product.productNumber}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </Hidden>
                        <StyledWrapper {...styles.modalCarousalWrapper}>
                            {product?.images && product?.images?.length && (
                                <ProductImageCarousel
                                    selectedIndex={selectedImageIndex}
                                    onSelectImage={selectImageHandler}
                                    extendedStyles={styles.imageCarousel}
                                    inModal={true}
                                />
                            )}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
